import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { scan, startWith } from 'rxjs';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import {
  ISHIKAWA_BONE_COLORS,
  ISHIKAWA_BONE_ICONS,
  ISHIKAWA_TEMPLATE_DEFAULT_BONE_ICON,
  ISHIKAWA_TEMPLATE_MAX_BONE_COUNT,
  ISHIKAWA_TEMPLATE_MIN_BONE_COUNT,
} from '../ishikawa-templates.constants';
import {
  EIshikawaTemplateDialogMode,
  IIshikawaTemplateBoneRequest,
  IIshikawaTemplateDialogData,
  IIshikawaTemplateRequest,
  TIshikawaTemplateBoneForm,
  TIshikawaTemplateForm,
} from '../ishikawa-templates.interface';
import { IshikawaTemplatesStore } from '../ishikawa-templates.store';

/** One bone row of the editor, with everything the template shows precomputed. */
interface IBoneRow {
  control: TIshikawaTemplateBoneForm;
  color: string;
  icon: string;
  /** Set on the first bone above and below the spine: the "Top" / "Bottom" caption. */
  sectionLabelKey: string | null;
}

const NAME_MAX_LENGTH = 255;
const I18N = 'page.ishikawaTemplates';

/** Bone names are compared trimmed and case-insensitively, the way the API compares them. */
function uniqueBoneNamesValidator(control: AbstractControl): ValidationErrors | null {
  const names: string[] = (control as FormArray<TIshikawaTemplateBoneForm>).controls
    .map((bone: TIshikawaTemplateBoneForm) => bone.controls.name.value.trim().toLocaleLowerCase())
    .filter(Boolean);

  return new Set(names).size === names.length ? null : { duplicateBoneNames: true };
}

/**
 * Add / edit / view dialog of an Ishikawa template: its name and its bones (2 to 8, in fishbone order).
 * Opened with the page injector, so saving goes through the page's store; the page closes the dialog once saved.
 */
@Component({
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatMenu,
    MatMenuContent,
    MatMenuTrigger,
    MatTooltip,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-ishikawa-template-dialog',
  styleUrl: './ishikawa-template-dialog.component.scss',
  templateUrl: './ishikawa-template-dialog.component.html',
})
export class IshikawaTemplateDialogComponent {
  public static readonly dialogParameters = {
    disableClose: true,
    maxWidth: '100%',
    restoreFocus: false,
    width: EDialogWidth.medium,
  };

  private readonly store = inject(IshikawaTemplatesStore);
  private readonly translate = inject(TranslateService);
  public readonly data: IIshikawaTemplateDialogData = inject(MAT_DIALOG_DATA);

  public readonly isReadOnly: boolean = this.data.mode === EIshikawaTemplateDialogMode.view;
  public readonly title: string = this.getTitle();
  public readonly submitButtonText: string =
    this.data.mode === EIshikawaTemplateDialogMode.edit
      ? this.translate.instant('button.saveChanges')
      : this.getContextText('button.addContext');
  public readonly hintParams = { max: ISHIKAWA_TEMPLATE_MAX_BONE_COUNT, min: ISHIKAWA_TEMPLATE_MIN_BONE_COUNT };
  public readonly colors: readonly string[] = ISHIKAWA_BONE_COLORS;
  public readonly icons: readonly string[] = ISHIKAWA_BONE_ICONS;
  public readonly isSaving: Signal<boolean> = this.store.isSaving;

  public readonly form: TIshikawaTemplateForm = new FormGroup({
    bones: new FormArray<TIshikawaTemplateBoneForm>(
      this.data.bones.map((bone: IIshikawaTemplateBoneRequest) => this.createBoneForm(bone)),
      { validators: uniqueBoneNamesValidator },
    ),
    name: new FormControl(this.data.name, {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(NAME_MAX_LENGTH)],
    }),
  });

  /** Re-evaluated on every edit and on add / remove / reorder (all of which emit valueChanges). */
  private readonly formChange: Signal<number> = toSignal(
    this.form.valueChanges.pipe(
      scan((count: number) => count + 1, 0),
      startWith(0),
    ),
    { requireSync: true },
  );

  public readonly boneRows: Signal<IBoneRow[]> = computed(() => {
    this.formChange();

    const { controls }: { controls: TIshikawaTemplateBoneForm[] } = this.form.controls.bones;
    const topCount: number = Math.ceil(controls.length / 2);

    return controls.map((control: TIshikawaTemplateBoneForm, index: number) => ({
      color: control.controls.color.value,
      control,
      icon: control.controls.icon.value,
      sectionLabelKey: index === 0 ? `${I18N}.top` : index === topCount ? `${I18N}.bottom` : null,
    }));
  });
  public readonly canAddBone: Signal<boolean> = computed(
    () => this.boneRows().length < ISHIKAWA_TEMPLATE_MAX_BONE_COUNT,
  );
  public readonly canRemoveBone: Signal<boolean> = computed(
    () => this.boneRows().length > ISHIKAWA_TEMPLATE_MIN_BONE_COUNT,
  );
  public readonly hasDuplicateBoneNames: Signal<boolean> = computed(() => {
    this.formChange();

    return this.form.controls.bones.hasError('duplicateBoneNames');
  });

  constructor() {
    if (this.isReadOnly) {
      this.form.disable();
    }
  }

  public onAddBone(): void {
    if (!this.canAddBone()) {
      return;
    }

    const usedColors: Set<string> = new Set(this.boneRows().map((row: IBoneRow) => row.color));

    this.form.controls.bones.push(
      this.createBoneForm({
        color: this.colors.find((color: string) => !usedColors.has(color)) ?? this.colors[0],
        icon: ISHIKAWA_TEMPLATE_DEFAULT_BONE_ICON,
        name: '',
      }),
    );
  }

  public onRemoveBone(index: number): void {
    if (this.canRemoveBone()) {
      this.form.controls.bones.removeAt(index);
    }
  }

  public onBoneDrop(event: CdkDragDrop<IBoneRow[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const { bones }: { bones: FormArray<TIshikawaTemplateBoneForm> } = this.form.controls;
    const moved: TIshikawaTemplateBoneForm = bones.at(event.previousIndex);

    bones.removeAt(event.previousIndex, { emitEvent: false });
    bones.insert(event.currentIndex, moved);
  }

  public onSelectColor(index: number, color: string): void {
    this.form.controls.bones.at(index).controls.color.setValue(color);
  }

  public onSelectIcon(index: number, icon: string): void {
    this.form.controls.bones.at(index).controls.icon.setValue(icon);
  }

  public onSubmit(): void {
    if (this.isReadOnly || this.isSaving()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const request: IIshikawaTemplateRequest = {
      bones: this.form.controls.bones.controls.map((bone: TIshikawaTemplateBoneForm) => ({
        color: bone.controls.color.value,
        icon: bone.controls.icon.value,
        name: bone.controls.name.value.trim(),
        ...(bone.controls.id.value === null ? {} : { id: bone.controls.id.value }),
      })),
      name: this.form.controls.name.value.trim(),
    };

    if (this.data.mode === EIshikawaTemplateDialogMode.edit && this.data.id !== null) {
      this.store.updateOne({ dto: request, id: this.data.id });
    } else {
      this.store.createOne(request);
    }
  }

  private createBoneForm(bone: IIshikawaTemplateBoneRequest): TIshikawaTemplateBoneForm {
    return new FormGroup({
      color: new FormControl(bone.color, { nonNullable: true }),
      icon: new FormControl(bone.icon, { nonNullable: true }),
      id: new FormControl<number | null>(bone.id ?? null),
      name: new FormControl(bone.name, {
        nonNullable: true,
        validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(NAME_MAX_LENGTH)],
      }),
    });
  }

  private getTitle(): string {
    switch (this.data.mode) {
      case EIshikawaTemplateDialogMode.view:
        return this.data.name;
      case EIshikawaTemplateDialogMode.edit:
        return this.getContextText('button.editContext');
      default:
        return this.getContextText('button.addContext');
    }
  }

  private getContextText(key: string): string {
    return this.translate.instant(key, { context: this.translate.instant('field.ishikawaTemplate') });
  }
}
